import { create } from 'zustand';
import { unstable_batchedUpdates } from 'react-dom';
import type { TimelineProject, AudioClip, VideoShot, Comment, Approval } from '@/types/timeline.ts';
import type { VoiceAssignment } from '@/types/tts.ts';
import { useProjectStore } from './projectStore.ts';
import { useAudioStore } from './audioStore.ts';
import { useCommentStore } from './commentStore.ts';
import { useAiStore } from './aiStore.ts';

// ── Snapshot types ──────────────────────────────────────────────────────

export interface UndoSnapshot {
  project: TimelineProject | null;
  audioClips: AudioClip[];
  videoShots: VideoShot[];
  comments: Comment[];
  approvals: Approval[];
  voiceAssignments: VoiceAssignment[];
  aiAudioHints: Record<number, string>;
}

export interface UndoOptions {
  pushUndo?: boolean;
}

// Cap undo depth. Each snapshot shallow-copies the scene graph + audio/comment
// arrays and pins references to any entities deleted since (shots, clips,
// transitions) until it falls off the stack — so a deep stack keeps removed
// objects alive in embedded WebKit's tight heap. 25 balances useful history
// against memory. clearStacks() runs on every project load (actions.ts).
const MAX_UNDO = 25;

/** Shallow-copy project arrays; scene/shot objects are already immutable (Zustand pattern) */
export function takeSnapshot(state: {
  project: TimelineProject | null;
  audioClips: AudioClip[];
  videoShots: VideoShot[];
  comments: Comment[];
  approvals: Approval[];
  voiceAssignments: VoiceAssignment[];
  aiAudioHints: Record<number, string>;
}): UndoSnapshot {
  return {
    project: state.project ? {
      ...state.project,
      scenes: [...state.project.scenes],
      transitions: [...state.project.transitions],
    } : null,
    audioClips: [...state.audioClips],
    videoShots: [...state.videoShots],
    comments: [...state.comments],
    approvals: [...state.approvals],
    voiceAssignments: [...state.voiceAssignments],
    aiAudioHints: { ...state.aiAudioHints },
  };
}

// ── Store ────────────────────────────────────────────────────────────────

interface UndoState {
  _undoStack: UndoSnapshot[];
  _redoStack: UndoSnapshot[];

  pushSnapshot: () => void;
  undo: () => void;
  redo: () => void;
  canUndo: () => boolean;
  canRedo: () => boolean;
  clearStacks: () => void;
}

export const useUndoStore = create<UndoState>((set, get) => ({
  _undoStack: [],
  _redoStack: [],

  pushSnapshot: () => {
    const aiState = useAiStore.getState();
    const snap = takeSnapshot({
      project: useProjectStore.getState().project,
      audioClips: useAudioStore.getState().audioClips,
      videoShots: useProjectStore.getState().videoShots,
      comments: useCommentStore.getState().comments,
      approvals: useCommentStore.getState().approvals,
      voiceAssignments: aiState.voiceAssignments,
      aiAudioHints: aiState.aiAudioHints,
    });
    const stack = [...get()._undoStack, snap];
    if (stack.length > MAX_UNDO) stack.shift();
    set({ _undoStack: stack, _redoStack: [] });
  },

  undo: () => {
    const { _undoStack, _redoStack } = get();
    if (_undoStack.length === 0) return;

    const snap = _undoStack[_undoStack.length - 1];
    const curAi = useAiStore.getState();
    const currentSnap = takeSnapshot({
      project: useProjectStore.getState().project,
      audioClips: useAudioStore.getState().audioClips,
      videoShots: useProjectStore.getState().videoShots,
      comments: useCommentStore.getState().comments,
      approvals: useCommentStore.getState().approvals,
      voiceAssignments: curAi.voiceAssignments,
      aiAudioHints: curAi.aiAudioHints,
    });

    unstable_batchedUpdates(() => {
      useProjectStore.setState({
        project: snap.project,
        videoShots: snap.videoShots,
      });
      useAudioStore.setState({ audioClips: snap.audioClips });
      useCommentStore.setState({
        comments: snap.comments,
        approvals: snap.approvals,
      });
      useAiStore.setState({
        voiceAssignments: snap.voiceAssignments,
        aiAudioHints: snap.aiAudioHints,
      });
      set({
        _undoStack: _undoStack.slice(0, -1),
        _redoStack: [..._redoStack, currentSnap],
      });
    });
  },

  redo: () => {
    const { _undoStack, _redoStack } = get();
    if (_redoStack.length === 0) return;

    const snap = _redoStack[_redoStack.length - 1];
    const curAi = useAiStore.getState();
    const currentSnap = takeSnapshot({
      project: useProjectStore.getState().project,
      audioClips: useAudioStore.getState().audioClips,
      videoShots: useProjectStore.getState().videoShots,
      comments: useCommentStore.getState().comments,
      approvals: useCommentStore.getState().approvals,
      voiceAssignments: curAi.voiceAssignments,
      aiAudioHints: curAi.aiAudioHints,
    });

    unstable_batchedUpdates(() => {
      useProjectStore.setState({
        project: snap.project,
        videoShots: snap.videoShots,
      });
      useAudioStore.setState({ audioClips: snap.audioClips });
      useCommentStore.setState({
        comments: snap.comments,
        approvals: snap.approvals,
      });
      useAiStore.setState({
        voiceAssignments: snap.voiceAssignments,
        aiAudioHints: snap.aiAudioHints,
      });
      set({
        _undoStack: [..._undoStack, currentSnap],
        _redoStack: _redoStack.slice(0, -1),
      });
    });
  },

  canUndo: () => get()._undoStack.length > 0,
  canRedo: () => get()._redoStack.length > 0,
  clearStacks: () => set({ _undoStack: [], _redoStack: [] }),
}));
